import EnrollmentQuestionnaire from './EnrollmentQuestionnaire'
import ModalComponent from '../common/Modal/Modal.component'
import { MsPlanDetails } from '../../interface/enrollmentApplication/MsPlanDetails'
import React from 'react'
import { SnackbarTypes } from '../../enums/SnackbarTypesEnum'
import customerStore from '../../datastore/CustomerStore'
import { getCustomerDetails } from '../../service/customer'
import snackbarStore from '../../datastore/SnackbarStore'

interface ShowModalParams {
  planId: string
  forSpouse: boolean
  isEappEnabled: boolean
  planDetails: MsPlanDetails
}

interface ModalWrapperReturn {
  showEAppModal: (params: ShowModalParams) => void
  EnrollmentApplicationModal: React.FC
  enrollmentApplicationKey: string | null | undefined
  spouseEnrollmentApplicationKey: string | null | undefined
}

const useEnrollmentApplicationModal = (
  csgEnrollmentApplicationKey: string | null | undefined,
  csgSpouseEnrollmentApplicationKey: string | undefined | null
): ModalWrapperReturn => {
  const [isOpen, setIsOpen] = React.useState(false)
  const [currentParams, setCurrentParams] =
    React.useState<ShowModalParams | null>(null)
  const [enrollmentApplicationKey, setEnrollmentApplicationKey] =
    React.useState(csgEnrollmentApplicationKey)
  const [spouseEnrollmentApplicationKey, setSpouseEnrollmentApplicationKey] =
    React.useState(csgSpouseEnrollmentApplicationKey)
  const hasBeenOpenedRef = React.useRef(false)
  // Set when the wizard actually upgraded the session (LITE -> lead) - either
  // early, mid-wizard (EnrollmentQuestionnaire's onLiteSessionUpgraded), or as
  // a side effect of a full e-app submission (submitEnrollmentApplication's
  // own pre-submit upgrade). Reset each time a fresh wizard is opened.
  const shouldRefreshCustomerOnCloseRef = React.useRef(false)

  // customerStore doesn't know about a lead conversion that happened behind
  // the scenes mid-wizard. Once the modal actually closes (submit success,
  // error, or manual close - all paths land here since they all flip isOpen),
  // and only if an upgrade actually happened this wizard session, pull the
  // latest profile so email/phone/status are fresh for whatever the customer
  // does next (e.g. clicking Enroll on a plan card). Deferred until close so
  // a status flip to 'new' doesn't pop the Dashboard's Reset Password modal
  // mid-wizard.
  React.useEffect(() => {
    if (isOpen) {
      hasBeenOpenedRef.current = true
      return
    }
    if (!hasBeenOpenedRef.current) return
    hasBeenOpenedRef.current = false

    if (!shouldRefreshCustomerOnCloseRef.current) return
    shouldRefreshCustomerOnCloseRef.current = false

    getCustomerDetails()
      .then((data) => customerStore.set(data))
      .catch((error) => {
        console.error(
          'Failed to refresh customer details after e-app modal close:',
          error
        )
      })
  }, [isOpen])

  const showEAppModal = React.useCallback(
    ({ planId, forSpouse, isEappEnabled, planDetails }: ShowModalParams) => {
      if (!isEappEnabled) {
        // Early return - do nothing if isEappEnabled is false
        return
      }

      shouldRefreshCustomerOnCloseRef.current = false
      setCurrentParams({ planId, forSpouse, isEappEnabled, planDetails })
      setIsOpen(true)
    },
    []
  )

  const EnrollmentApplicationModal: React.FC = React.useCallback(() => {
    if (!isOpen || !currentParams) {
      return null
    }

    return (
      <ModalComponent
        setOpen={setIsOpen}
        width='800px'
        padding='20px 20px 0px 20px'
        hideCloseButton={false}
        boldScroll={true}
        description={`Enrollment Questionnaire Modal for plan ${
          currentParams.planId
        }${currentParams.forSpouse ? ' (spouse)' : ''}`}
      >
        <EnrollmentQuestionnaire
          planId={currentParams.planId}
          forSpouse={currentParams.forSpouse}
          onLiteSessionUpgraded={() => {
            shouldRefreshCustomerOnCloseRef.current = true
          }}
          onSuccess={(enrollmentApplicationKey: string) => {
            snackbarStore.set({
              snackbarMessage:
                'Enrollment Questionnaire submitted successfully!',
              snackbarOpen: true,
              snackbarType: SnackbarTypes.SUCCESS,
            })
            setIsOpen(false)
            if (!currentParams.forSpouse) {
              setEnrollmentApplicationKey(enrollmentApplicationKey)
            } else {
              setSpouseEnrollmentApplicationKey(enrollmentApplicationKey)
            }
            if (currentParams.forSpouse) {
              customerStore.set({
                isSpouseMSEnrollmentApplicationSubmitted: true,
              })
            } else {
              customerStore.set({ isMSEnrollmentApplicationSubmitted: true })
            }
          }}
          onError={(error) => {
            console.error('Enrollment Questionnaire error:', error)
            snackbarStore.set({
              snackbarMessage: error,
              snackbarOpen: true,
              snackbarType: SnackbarTypes.ERROR,
            })
            setIsOpen(false)
          }}
          planDetails={currentParams.planDetails}
        />
      </ModalComponent>
    )
  }, [isOpen, currentParams])

  return {
    showEAppModal,
    EnrollmentApplicationModal,
    enrollmentApplicationKey,
    spouseEnrollmentApplicationKey,
  }
}

export default useEnrollmentApplicationModal
